import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type { LedgerResponse, LedgerQueryParams } from "../types";

interface UseGeneralLedgerResult {
  ledger: LedgerResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch general ledger for a specific account.
 *
 * Returns null if accountId is not provided (allows for initial empty state).
 */
export function useGeneralLedger(
  params: Partial<LedgerQueryParams>
): UseGeneralLedgerResult {
  const [ledger, setLedger] = useState<LedgerResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const { accountId, startDate, endDate } = params;

  useEffect(() => {
    // Don't fetch if required params are not provided
    if (!accountId || !startDate || !endDate) {
      setLedger(null);
      setIsLoading(false);
      return;
    }

    const fetchLedger = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();
        queryParams.set("accountId", accountId);
        queryParams.set("startDate", startDate);
        queryParams.set("endDate", endDate);

        const url = `/api/general-ledger?${queryParams.toString()}`;
        const data = await apiFetch.get<LedgerResponse>(url);
        setLedger(data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setLedger(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLedger();
  }, [fetchKey, accountId, startDate, endDate]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    ledger,
    isLoading,
    error,
    refetch,
  };
}
